import React, { useState, useEffect, useCallback} from "react";
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, Switch, Button, CircularProgress, Snackbar, Alert, TableContainer,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import {
  getConfiguracionCupo, guardarConfiguracionCupo,
} from "../../componentes/http/actions/DocumentoAction";


const ConfiguracionCupoAnalizador = () => {
  const token = localStorage.getItem("token");
  const [rows, setRows] = useState(null);
  const [guardando, setGuardando] = useState(null); 
  const [toast, setToast] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const data = await getConfiguracionCupo(token);
      setRows(data.map((d) => ({
        idCliente: d.idCliente ?? d.IdCliente,
        nombreCliente: d.nombreCliente ?? d.NombreCliente,
        limiteSemanal: d.limiteSemanal ?? d.LimiteSemanal ?? 0,
        activo: (d.activo ?? d.Activo) !== false,
        _dirty: false,
      })));
    } catch {
      setRows([]);
      setToast({ tipo: "error", msg: "No se pudo cargar la configuración." });
    }
  }, [token]);
    
  useEffect(() => { cargar(); }, [cargar]);

  const cambiar = (idCliente, campo, valor) => {
    setRows((prev) => prev.map((r) =>
      r.idCliente === idCliente ? { ...r, [campo]: valor, _dirty: true } : r
    ));
  };

  const guardar = async (row) => {
    setGuardando(row.idCliente);
    try {
      await guardarConfiguracionCupo(token, {
        idCliente: row.idCliente,
        limiteSemanal: Number(row.limiteSemanal),
        activo: row.activo,
      });
      setRows((prev) => prev.map((r) => r.idCliente === row.idCliente ? { ...r, _dirty: false } : r));
      setToast({ tipo: "success", msg: "Configuración guardada." });
    } catch (e) {
      setToast({ tipo: "error", msg: e?.response?.data?.mensaje || "No se pudo guardar." });
    } finally {
      setGuardando(null);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
        <TuneIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Cupo del analizador</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define cuántas normas por semana puede analizar cada usuario de un cliente. El límite
        se aplica por usuario y el cupo se reinicia cada lunes. Desactiva el interruptor para
        dar uso ilimitado a un cliente.
      </Typography>

      {rows === null ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Límite semanal</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Cupo activo</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.idCliente} hover>
                  <TableCell>{r.nombreCliente}</TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      size="small"
                      value={r.limiteSemanal}
                      disabled={!r.activo}
                      onChange={(e) => cambiar(r.idCliente, "limiteSemanal", e.target.value)}
                      inputProps={{ min: 0, style: { textAlign: "center", width: 70 } }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={r.activo}
                      onChange={(e) => cambiar(r.idCliente, "activo", e.target.checked)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!r._dirty || guardando === r.idCliente}
                      onClick={() => guardar(r)}
                    >
                      {guardando === r.idCliente ? <CircularProgress size={18} /> : "Guardar"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast && <Alert severity={toast.tipo} onClose={() => setToast(null)}>{toast.msg}</Alert>}
      </Snackbar>
    </Box>
  );
};

export default ConfiguracionCupoAnalizador;